<template>
  <v-container class="pt-10">
    <v-progress-linear indeterminate />
  </v-container>
</template>

<script setup>
import useApi from '@/shared/composables/useApi';
import { useStore } from 'vuex';
import useEventBus from '@/shared/composables/useEventBus';
import { useRoute, useRouter } from 'vue-router';

const { api } = useApi();
const familySubsidyProgram = ref(null);
const eventBus = useEventBus();
const route = useRoute();
const router = useRouter();
const store = useStore();
const $bct = inject('translator');

onMounted(async () => {
  await loadFamilySubsidyProgram();
  if (!familySubsidyProgram.value?.published_at && !isInTestMode()) {
    router.replace({ path: '/' });
    return;
  }
  store.dispatch('identify', {
    api: api,
    success: identifySuccess,
    failure: identifyFailure,
  });
});

function identifyFailure() {
  const familySubsidyProgramId = getFamilySubsidyProgramId();
  const redirect = {
    name: 'FamilySubsidyNew',
    params: { familySubsidyProgramId: familySubsidyProgramId },
  };
  window.localStorage.setItem('redirect', JSON.stringify(redirect));
  const mode = store.state.site.features.enable_registration ? 'signup' : 'login';
  router.replace({
    name: 'Signup',
    query: {
      action: 'familyApply',
      familySubsidyProgramId: familySubsidyProgramId,
      mode,
      redirect_url: route.fullPath,
    },
  });
}

async function identifySuccess() {
  if (store.state.is_anonymous) {
    identifyFailure();
    return;
  }

  const { data } = await api.parent.family_subsidy.index({
    family_subsidy_program_id: getFamilySubsidyProgramId(),
  });
  const hasSubmittedApplication = data.some((familySubsidy) => familySubsidy.submitted_at);
  if (hasSubmittedApplication && !familySubsidyProgram.value.enable_multiple_applications) {
    eventBus.chime("You've already applied for this program.");
    router.replace({ name: 'Dashboard' });
    return;
  }

  const inProgressSubsidy = data.find((familySubsidy) => !familySubsidy.submitted_at);
  if (inProgressSubsidy) {
    await routeToFamilySubsidy(inProgressSubsidy);
    return;
  }

  await startNewSubsidy();
}

function isInTestMode() {
  return route.query.test === 'true';
}

function getFamilySubsidyProgramId() {
  return route.params.familySubsidyProgramId;
}

async function loadFamilySubsidyProgram() {
  const resp = await api.public_api.organization.family_subsidy_program.get(
    getFamilySubsidyProgramId(),
  );
  familySubsidyProgram.value = resp.data;
}

async function routeToFamilySubsidy(subsidy) {
  await router.replace({ name: 'FamilySubsidyContinue', params: { id: subsidy.id } });
}

async function startNewSubsidy() {
  const resp = await api.parent.family_subsidy
    .create({ family_subsidy_program_id: getFamilySubsidyProgramId() })
    .catch((error) => {
      if (error.response?.data?.errors[0] === 'Family subsidy program has already been taken') {
        eventBus.error({
          response: { data: { errors: [$bct('Application already submitted')] } },
        });
      } else {
        eventBus.error(error);
      }
      router.replace({ name: 'Dashboard' });
    });
  if (!resp?.data) return;

  await routeToFamilySubsidy(resp.data);
}
</script>
